import { create } from "zustand";
import type { EditorTarget, ZonedTimeString } from "@poi-planner/shared";

export const DEFAULT_SIDEBAR_WIDTH = 320;
export const MIN_SIDEBAR_WIDTH = 288;
export const MAX_SIDEBAR_WIDTH = 560;
export const SIDEBAR_WIDTH_STORAGE_KEY = "plot-plan-go-sidebar-width";
export const PANE_VISIBILITY_STORAGE_KEY = "plot-plan-go-pane-visibility";
export const PLANNER_WIDTH_STORAGE_KEY = "plot-plan-go-planner-width";
export const DEFAULT_PLANNER_WIDTH = 520;
export const MIN_PLANNER_WIDTH = 420;
export const MAX_PLANNER_WIDTH = 1200;

export type TripPane = "sidebar" | "planner" | "map";
export type PlannerMode = "day" | "leg" | "all" | "calendar" | "agenda";

export type PoiLegPickerRequest =
  | { kind: "manage-pin"; poiId: string }
  | { kind: "schedule-overlap"; poiId: string; candidateLegIds: string[] };

export interface PaneVisibility {
  sidebar: boolean;
  planner: boolean;
  map: boolean;
}

const DEFAULT_PANE_VISIBILITY: PaneVisibility = {
  sidebar: true,
  planner: true,
  map: true,
};

export function clampSidebarWidth(width: number): number {
  return Math.min(
    MAX_SIDEBAR_WIDTH,
    Math.max(MIN_SIDEBAR_WIDTH, Math.round(width)),
  );
}

function readStoredSidebarWidth(): number {
  try {
    const storedWidth = Number(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    return Number.isFinite(storedWidth) && storedWidth > 0
      ? clampSidebarWidth(storedWidth)
      : DEFAULT_SIDEBAR_WIDTH;
  } catch {
    return DEFAULT_SIDEBAR_WIDTH;
  }
}

function storeSidebarWidth(width: number): void {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // The in-memory preference still works when storage is unavailable.
  }
}

export function clampPlannerWidth(width: number): number {
  return Math.min(
    MAX_PLANNER_WIDTH,
    Math.max(MIN_PLANNER_WIDTH, Math.round(width)),
  );
}

function readStoredPlannerWidth(): number {
  try {
    const storedWidth = Number(localStorage.getItem(PLANNER_WIDTH_STORAGE_KEY));
    return Number.isFinite(storedWidth) && storedWidth > 0
      ? clampPlannerWidth(storedWidth)
      : DEFAULT_PLANNER_WIDTH;
  } catch {
    return DEFAULT_PLANNER_WIDTH;
  }
}

function storePlannerWidth(width: number): void {
  try {
    localStorage.setItem(PLANNER_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // The in-memory preference still works when storage is unavailable.
  }
}

export function hasVisiblePane(visibility: PaneVisibility): boolean {
  return visibility.sidebar || visibility.planner || visibility.map;
}

export function parsePaneVisibility(value: string | null): PaneVisibility {
  if (!value) return DEFAULT_PANE_VISIBILITY;
  try {
    const parsed = JSON.parse(value) as Partial<PaneVisibility>;
    const visibility = {
      sidebar:
        typeof parsed.sidebar === "boolean"
          ? parsed.sidebar
          : DEFAULT_PANE_VISIBILITY.sidebar,
      planner:
        typeof parsed.planner === "boolean"
          ? parsed.planner
          : DEFAULT_PANE_VISIBILITY.planner,
      map:
        typeof parsed.map === "boolean"
          ? parsed.map
          : DEFAULT_PANE_VISIBILITY.map,
    };
    return hasVisiblePane(visibility) ? visibility : DEFAULT_PANE_VISIBILITY;
  } catch {
    return DEFAULT_PANE_VISIBILITY;
  }
}

function readStoredPaneVisibility(): PaneVisibility {
  try {
    return parsePaneVisibility(
      localStorage.getItem(PANE_VISIBILITY_STORAGE_KEY),
    );
  } catch {
    return DEFAULT_PANE_VISIBILITY;
  }
}

function storePaneVisibility(visibility: PaneVisibility): void {
  try {
    localStorage.setItem(
      PANE_VISIBILITY_STORAGE_KEY,
      JSON.stringify(visibility),
    );
  } catch {
    // The in-memory preference still works when storage is unavailable.
  }
}

export interface EventTimingProposal {
  eventType: "scheduledVisit" | "note";
  entityId: string;
  gesture: "move" | "resize";
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
  originalDisplayZone: string;
  targetDisplayZone: string;
}

export type NoteDraftTiming =
  | {
      type: "timed";
      startTime: ZonedTimeString;
      endTime: ZonedTimeString;
    }
  | {
      type: "all-day";
      startDate: string;
      endDate: string;
    };

/**
 * Prefilled fields for a POI being created from a non-form entry point, such
 * as clicking a basemap point of interest on the map. Read by POIEditorModal
 * when activeEditor is a "poi" create target.
 */
export interface PoiDraftSeed {
  name: string;
  lat: number;
  lng: number;
  googleMapsUrl?: string;
}

// ── UI State ──
interface UIState {
  // Editor state - unified pattern for all editors
  activeEditor: EditorTarget | null;
  timingProposal: EventTimingProposal | null;

  // Blocking prompt for locked scheduled POI actions
  lockedActionPrompt: string | null;

  // Leg membership is separate from entity editors so a scheduling action can
  // finish and close its editor without also dismissing this follow-up choice.
  poiLegPickerRequest: PoiLegPickerRequest | null;

  // Layout
  paneVisibility: PaneVisibility;
  sidebarWidth: number;
  plannerWidth: number;
  plannerMode: PlannerMode;

  // Date and explicit leg selected by the Planner.
  plannerFocusDate: string | null;
  plannerCalendarStart: string | null;
  activeLegId: string | null;

  // Night selected by an accommodation banner/calendar stay
  accommodationPickerDate: string | null;

  // Proximity sorting target (for future use)
  targetPoiId: string | null;

  // POI currently highlighted/flown-to on whichever map pane is visible;
  // independent of targetPoiId so selecting/flying never changes sort order
  selectedMapPoiId: string | null;

  // Actions
  openEditor: (target: EditorTarget) => void;
  closeEditor: () => void;

  // Prefilled timing for a note being created from a calendar gesture (read
  // by NoteEditorModal when activeEditor is a "note" create target).
  noteDraftTiming: NoteDraftTiming | null;

  // Prefilled fields for a POI being created from outside the editor (read by
  // POIEditorModal when activeEditor is a "poi" create target).
  poiDraftSeed: PoiDraftSeed | null;

  // Convenience methods for opening specific editors
  openPOIEditor: (
    mode: "create" | "edit",
    poiId?: string,
    seed?: PoiDraftSeed,
  ) => void;
  openLegEditor: (legId: string) => void;
  /**
   * Set by a leg bar's focus button; the sidebar picks it up to filter to that
   * leg, then clears it.
   */
  pendingLegFocusId: string | null;
  setSidebarLegFocus: (legId: string | null) => void;
  openAccomEditor: (mode: "create" | "edit", accomId?: string) => void;
  openNoteEditor: (
    mode: "create" | "edit",
    noteId?: string,
    draftTiming?: NoteDraftTiming,
    timingProposal?: EventTimingProposal,
  ) => void;
  openScheduledVisitEditor: (
    scheduledPoiId: string,
    timingProposal?: EventTimingProposal,
  ) => void;

  // Other actions
  showLockedActionPrompt: (message: string) => void;
  clearLockedActionPrompt: () => void;
  openPoiLegPicker: (request: PoiLegPickerRequest) => void;
  closePoiLegPicker: () => void;
  setPaneVisible: (pane: TripPane, visible: boolean) => void;
  togglePane: (pane: TripPane) => void;
  setSidebarWidth: (width: number) => void;
  setPlannerWidth: (width: number) => void;
  setPlannerMode: (mode: PlannerMode) => void;
  setPlannerFocusDate: (date: string | null) => void;
  setPlannerCalendarStart: (date: string | null) => void;
  setActiveLegId: (legId: string | null) => void;
  openAccommodationPicker: (date: string) => void;
  closeAccommodationPicker: () => void;
  setTargetPoiId: (poiId: string | null) => void;
  setSelectedMapPoiId: (poiId: string | null) => void;
}

// Cleared whenever the active editor changes, so a proposal or drag-create
// draft from one editor can't leak into the next.
const CLEAR_EDITOR_SCRATCH = {
  timingProposal: null,
  noteDraftTiming: null,
  poiDraftSeed: null,
} as const;

// ── Store ──
export const useUIStore = create<UIState>((set) => ({
  activeEditor: null,
  timingProposal: null,
  noteDraftTiming: null,
  poiDraftSeed: null,
  lockedActionPrompt: null,
  poiLegPickerRequest: null,
  paneVisibility: readStoredPaneVisibility(),
  sidebarWidth: readStoredSidebarWidth(),
  plannerWidth: readStoredPlannerWidth(),
  plannerMode: "all",
  plannerFocusDate: null,
  plannerCalendarStart: null,
  activeLegId: null,
  accommodationPickerDate: null,
  targetPoiId: null,
  pendingLegFocusId: null,
  selectedMapPoiId: null,

  // Generic editor actions
  openEditor: (target) =>
    set({ activeEditor: target, ...CLEAR_EDITOR_SCRATCH }),
  closeEditor: () => set({ activeEditor: null, ...CLEAR_EDITOR_SCRATCH }),

  // Convenience methods that construct EditorTarget
  openLegEditor: (legId) =>
    set({
      activeEditor: { type: "leg", mode: "edit", entityId: legId },
      ...CLEAR_EDITOR_SCRATCH,
    }),

  setSidebarLegFocus: (legId) => set({ pendingLegFocusId: legId }),

  openPOIEditor: (mode, poiId, seed) =>
    set({
      activeEditor: {
        type: "poi",
        mode,
        entityId: poiId ?? null,
      },
      ...CLEAR_EDITOR_SCRATCH,
      poiDraftSeed: mode === "create" ? (seed ?? null) : null,
    }),

  openAccomEditor: (mode, accomId) =>
    set({
      activeEditor: {
        type: "accommodation",
        mode,
        entityId: accomId ?? null,
      },
      ...CLEAR_EDITOR_SCRATCH,
    }),

  openNoteEditor: (mode, noteId, draftTiming, timingProposal) =>
    set({
      activeEditor: {
        type: "note",
        mode,
        entityId: noteId ?? null,
      },
      noteDraftTiming: draftTiming ?? null,
      timingProposal: timingProposal ?? null,
    }),

  openScheduledVisitEditor: (scheduledPoiId, timingProposal) =>
    set({
      activeEditor: {
        type: "scheduledVisit",
        mode: "edit",
        entityId: scheduledPoiId,
      },
      timingProposal: timingProposal ?? null,
      noteDraftTiming: null,
    }),

  // Other actions
  showLockedActionPrompt: (message) => set({ lockedActionPrompt: message }),
  clearLockedActionPrompt: () => set({ lockedActionPrompt: null }),
  openPoiLegPicker: (request) => set({ poiLegPickerRequest: request }),
  closePoiLegPicker: () => set({ poiLegPickerRequest: null }),
  setPaneVisible: (pane, visible) =>
    set((state) => {
      const paneVisibility = { ...state.paneVisibility, [pane]: visible };
      if (!hasVisiblePane(paneVisibility)) return state;
      storePaneVisibility(paneVisibility);
      return { paneVisibility };
    }),
  togglePane: (pane) =>
    set((state) => {
      const paneVisibility = {
        ...state.paneVisibility,
        [pane]: !state.paneVisibility[pane],
      };
      if (!hasVisiblePane(paneVisibility)) return state;
      storePaneVisibility(paneVisibility);
      return { paneVisibility };
    }),
  setSidebarWidth: (width) => {
    const sidebarWidth = clampSidebarWidth(width);
    storeSidebarWidth(sidebarWidth);
    set({ sidebarWidth });
  },
  setPlannerWidth: (width) => {
    const plannerWidth = clampPlannerWidth(width);
    storePlannerWidth(plannerWidth);
    set({ plannerWidth });
  },
  setPlannerMode: (mode) => set({ plannerMode: mode }),
  setPlannerFocusDate: (date) => set({ plannerFocusDate: date }),
  setPlannerCalendarStart: (date) => set({ plannerCalendarStart: date }),
  setActiveLegId: (legId) => set({ activeLegId: legId }),
  openAccommodationPicker: (date) => set({ accommodationPickerDate: date }),
  closeAccommodationPicker: () => set({ accommodationPickerDate: null }),
  setTargetPoiId: (poiId) => set({ targetPoiId: poiId }),
  setSelectedMapPoiId: (poiId) => set({ selectedMapPoiId: poiId }),
}));
